import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import LocalCarWashOutlinedIcon from '@mui/icons-material/LocalCarWashOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';

const DATA_SOURCES = [
  {
    id: 'mogoo',
    eyebrow: 'LIVE OPERATIONS',
    title: 'Mogoo 운영 기준',
    description: '앞으로의 가입·권한·운영 기록이 쌓일 기준입니다.',
    detail: '현재는 운영 준비 상태입니다. 현장 세차 권리를 판단하기 전, 전체 흐름 검증이 필요합니다.',
    status: '준비 중',
    freshness: '기준 시각: 각 Mogoo 운영 기록에서 확인',
    tone: 'info',
    icon: <MonitorHeartOutlinedIcon />,
  },
  {
    id: 'v1',
    eyebrow: 'LEGACY HISTORY',
    title: 'ModernWash V1 이력',
    description: '기존 회원·차량·구독·세차·결제 이력을 확인하는 읽기 전용 영역입니다.',
    detail: '민원·운영 확인용입니다. 신규 결제나 세차 권리를 판단하지 않습니다.',
    status: '읽기 전용',
    freshness: '신선도: 마지막 성공 동기화 시각을 V1 미러에서 확인',
    tone: 'legacy',
    icon: <SyncOutlinedIcon />,
  },
  {
    id: 'jj001',
    eyebrow: 'FIELD OBSERVATION',
    title: 'JJ001 현장 관측',
    description: '세차 일별 자료와 카메라·장비성 자료를 확인하는 외부 원천입니다.',
    detail: '회원·구독·결제의 기준 데이터가 아닙니다. 현장 상태를 대조할 때만 사용합니다.',
    status: '관측 전용',
    freshness: '수신 시각: 각 현장 관측 화면에서 확인',
    tone: 'observation',
    icon: <SensorsOutlinedIcon />,
  },
];

const QUICK_LINKS = [
  {
    title: '기존 회원 · 상세 이력 조회',
    detail: 'V1 회원, 차량, 과거 구독·세차·결제 이력을 전용 읽기 화면에서 확인합니다.',
    to: '/modernwash-ops/members',
    icon: <DirectionsCarOutlinedIcon />,
    source: 'ModernWash V1 라이브 읽기 전용',
  },
  {
    title: '운영 이력 대시보드',
    detail: 'V1 미러의 동기화 시각과 이력 기준을 먼저 확인합니다.',
    to: '/modernwash',
    icon: <SyncOutlinedIcon />,
    source: 'V1 읽기 전용 미러',
  },
  {
    title: '일일 세차 현황',
    detail: '하루의 실제 세차와 분류별 인식 이벤트를 분리해 봅니다.',
    to: '/modernwash/daily',
    icon: <LocalCarWashOutlinedIcon />,
    source: 'V1 읽기 전용 미러',
  },
  {
    title: '일별 세차 현황',
    detail: '기간별 실 세차와 결제 참고치를 확인합니다.',
    to: '/modernwash/range',
    icon: <ReceiptLongOutlinedIcon />,
    source: 'V1 읽기 전용 미러',
  },
  {
    title: '차량 원천 대조 · 진단',
    detail: '기존 구독 차량 정보를 외주 원천과 대조합니다. 정상 운영 기준으로 사용하지 않습니다.',
    to: '/modernwash/vehicle-list',
    icon: <DirectionsCarOutlinedIcon />,
    source: '외주 V1 라이브 조회',
  },
  {
    title: '세차 차량 원천 대조 · 진단',
    detail: '번호판 인식·세차 이벤트의 원천 기록을 대조합니다. 권리 승인 근거가 아닙니다.',
    to: '/modernwash/wash-log',
    icon: <LocalCarWashOutlinedIcon />,
    source: '외주 V1 라이브 조회',
  },
  {
    title: '세차요금 원천 대조 · 진단',
    detail: '과거 결제 내역을 민원·운영 확인 목적으로 대조합니다. 재무 원장이 아닙니다.',
    to: '/modernwash/payment-list',
    icon: <ReceiptLongOutlinedIcon />,
    source: '외주 V1 라이브 조회',
  },
  {
    title: '카메라 상태',
    detail: '현장 카메라 영상과 연결 상태를 확인합니다.',
    to: '/camera',
    icon: <CameraAltOutlinedIcon />,
    source: 'JJ001 현장 관측',
  },
  {
    title: 'LPR 인식 로그',
    detail: '인식 결과와 예외를 운영 기록과 대조합니다.',
    to: '/lpr-logs',
    icon: <SensorsOutlinedIcon />,
    source: 'Mogoo 운영 기록',
  },
  {
    title: '키오스크 상태',
    detail: '현장 키오스크의 결제·점검 상태를 확인합니다.',
    to: '/kiosk',
    icon: <MonitorHeartOutlinedIcon />,
    source: 'Mogoo 현장 연동',
  },
];

const TONES = {
  info: { color: '#2563EB', background: '#EFF6FF', border: '#BFDBFE' },
  legacy: { color: '#7C3AED', background: '#F5F3FF', border: '#DDD6FE' },
  observation: { color: '#B45309', background: '#FFFBEB', border: '#FDE68A' },
};

const SourceCard = ({ source }) => {
  const tone = TONES[source.tone];
  return (
    <Paper
      component="article"
      variant="outlined"
      sx={{
        p: 2.25,
        minHeight: 216,
        borderColor: tone.border,
        background: `linear-gradient(145deg, ${tone.background} 0%, #fff 72%)`,
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box
          aria-hidden="true"
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 40,
            height: 40,
            borderRadius: 2.5,
            color: tone.color,
            backgroundColor: alpha(tone.color, 0.1),
          }}
        >
          {source.icon}
        </Box>
        <Chip
          label={source.status}
          size="small"
          sx={{
            color: tone.color,
            border: `1px solid ${tone.border}`,
            backgroundColor: alpha('#fff', 0.82),
            fontWeight: 800,
          }}
        />
      </Stack>
      <Typography sx={{ mt: 2, color: tone.color, fontSize: 10.5, fontWeight: 900, letterSpacing: '0.11em' }}>
        {source.eyebrow}
      </Typography>
      <Typography component="h3" sx={{ mt: 0.5, fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em' }}>
        {source.title}
      </Typography>
      <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 13, lineHeight: 1.55 }}>
        {source.description}
      </Typography>
      <Typography sx={{ mt: 1.25, color: tone.color, fontSize: 11.5, fontWeight: 700, lineHeight: 1.45 }}>
        {source.detail}
      </Typography>
      <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 10.5, fontWeight: 700 }}>
        {source.freshness}
      </Typography>
    </Paper>
  );
};

const OperationLink = ({ link }) => (
  <Paper
    component={RouterLink}
    to={link.to}
    variant="outlined"
    sx={{
      display: 'flex',
      minHeight: 150,
      p: 2,
      textDecoration: 'none',
      color: 'inherit',
      borderColor: 'rgba(15, 23, 42, 0.10)',
      transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        borderColor: 'primary.light',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.09)',
      },
      '&:focus-visible': {
        outline: '3px solid rgba(37, 99, 235, 0.35)',
        outlineOffset: 2,
      },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&:hover': { transform: 'none' },
      },
    }}
  >
    <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
      <Box
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: 38,
          height: 38,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 2.25,
          color: 'primary.main',
          backgroundColor: 'rgba(37, 99, 235, 0.08)',
        }}
      >
        {link.icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.02em' }}>
            {link.title}
          </Typography>
          <ArrowForwardRoundedIcon aria-hidden="true" fontSize="small" sx={{ color: 'text.disabled' }} />
        </Stack>
        <Typography sx={{ mt: 0.65, color: 'text.secondary', fontSize: 12, lineHeight: 1.5 }}>
          {link.detail}
        </Typography>
        <Typography sx={{ mt: 1.25, color: 'text.disabled', fontSize: 10.5, fontWeight: 800 }}>
          출처 · {link.source}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

/**
 * ModernWash 전주점 운영 시작 화면.
 *
 * 의도적으로 데이터를 요청하지 않는 안내·탐색 셸이다. 각 하위 화면이 해당 원천의
 * 최신 상태와 수신 시각을 조회한다. 이 화면은 V1/JJ001 값을 Mogoo의 실제 운영
 * 권리나 결제 원장처럼 보이게 하지 않는다.
 */
const ModernWashOperationsPortal = () => (
  <Box component="main" sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 2.5, md: 4 } }}>
    <Box
      component="header"
      sx={{
        overflow: 'hidden',
        p: { xs: 2.5, md: 4 },
        borderRadius: 4,
        color: '#fff',
        background: 'radial-gradient(circle at 88% 12%, rgba(45,212,191,0.28), transparent 28%), linear-gradient(125deg, #0F172A 0%, #172554 55%, #1E3A8A 100%)',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ maxWidth: 680 }}>
          <Typography sx={{ color: '#A5F3FC', fontSize: 11, fontWeight: 900, letterSpacing: '0.13em' }}>
            MODERNWASH · JEONJU OPERATIONS
          </Typography>
          <Typography component="h1" sx={{ mt: 1, fontSize: { xs: 29, md: 38 }, fontWeight: 900, letterSpacing: '-0.045em', lineHeight: 1.15 }}>
            운영 판단의 기준을<br />
            한 곳에서 분명하게.
          </Typography>
          <Typography sx={{ mt: 1.5, color: 'rgba(226,232,240,0.88)', fontSize: { xs: 13, md: 14 }, lineHeight: 1.7 }}>
            Mogoo의 실운영 기록, ModernWash V1 과거 이력, JJ001 현장 관측 자료를 섞지 않고 구분해 확인합니다.
          </Typography>
        </Box>
        <Box
          sx={{
            alignSelf: { md: 'flex-end' },
            px: 1.5,
            py: 1.15,
            borderRadius: 2.5,
            border: '1px solid rgba(165,243,252,0.28)',
            backgroundColor: 'rgba(15, 23, 42, 0.32)',
          }}
        >
          <Typography sx={{ color: '#A5F3FC', fontSize: 10.5, fontWeight: 900, letterSpacing: '0.08em' }}>CURRENT SCOPE</Typography>
          <Typography sx={{ mt: 0.25, fontSize: 13, fontWeight: 700 }}>ModernWash 전주점 전용</Typography>
        </Box>
      </Stack>
    </Box>

    <Alert severity="info" variant="outlined" sx={{ mt: 2.5, borderRadius: 3, alignItems: 'center' }}>
      <Typography component="span" sx={{ fontSize: 13, fontWeight: 700 }}>
        현재는 전환 준비 단계입니다. V1·JJ001 자료는 확인과 대조에만 쓰며, Mogoo의 신규 권리·결제와 합산하지 않습니다.
      </Typography>
    </Alert>

    <Box component="section" aria-labelledby="source-boundary-title" sx={{ mt: 4 }}>
      <Typography id="source-boundary-title" component="h2" sx={{ fontSize: 21, fontWeight: 900, letterSpacing: '-0.035em' }}>
        데이터 원천과 사용 경계
      </Typography>
      <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 13 }}>
        각 화면에서 표시되는 마지막 수신 시각과 연결 상태를 확인한 뒤 운영 판단을 내리세요.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mt: 2 }}>
        {DATA_SOURCES.map((source) => <SourceCard key={source.id} source={source} />)}
      </Box>
    </Box>

    <Divider sx={{ my: 4 }} />

    <Box component="section" aria-labelledby="operation-menu-title">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-end' } }}>
        <Box>
          <Typography id="operation-menu-title" component="h2" sx={{ fontSize: 21, fontWeight: 900, letterSpacing: '-0.035em' }}>
            운영 바로가기
          </Typography>
          <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 13 }}>
            모두 조회 전용 화면입니다. 원천별 상태 문구와 갱신 시각은 각 화면에서 확인합니다.
          </Typography>
        </Box>
        <Chip label="읽기 전용 탐색" size="small" variant="outlined" sx={{ fontWeight: 800, alignSelf: { xs: 'flex-start', sm: 'auto' } }} />
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 1.5, mt: 2 }}>
        {QUICK_LINKS.map((link) => <OperationLink key={link.to} link={link} />)}
      </Box>
    </Box>
  </Box>
);

export default ModernWashOperationsPortal;
